import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { GradientAvatar } from "./GradientAvatar";
import { ChitChatEmptyState } from "./ChitChatEmptyState";
import { Users, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

type GroupConv = {
  id: string;
  name: string | null;
  avatar_url: string | null;
  description: string | null;
  member_count?: number;
};

export function CommunitiesPanel({
  onOpen,
  onCreate,
}: {
  onOpen: (id: string) => void;
  onCreate?: () => void;
}) {
  const { user } = useAuth();
  const [groups, setGroups] = useState<GroupConv[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: mems } = await supabase
        .from("chitchat_members")
        .select("conversation_id")
        .eq("user_id", user.id);
      const ids = (mems ?? []).map((m: any) => m.conversation_id);
      if (!ids.length) {
        if (!cancelled) { setGroups([]); setLoading(false); }
        return;
      }
      const { data: convs } = await supabase
        .from("chitchat_conversations")
        .select("id, name, avatar_url, description, is_group")
        .in("id", ids)
        .eq("is_group", true);
      if (cancelled) return;
      setGroups((convs as GroupConv[]) ?? []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-cc-ink">Communities</h2>
          <p className="text-xs text-cc-ink-soft">Group chats you belong to</p>
        </div>
        {onCreate && (
          <Button className="chitchat-btn-primary h-9 px-3" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="chitchat-surface h-16 animate-pulse" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="chitchat-surface p-8 text-center">
          <div className="mx-auto h-14 w-14 rounded-full chitchat-brand-bg flex items-center justify-center mb-3">
            <Users className="h-6 w-6 text-cc-cream" />
          </div>
          <h3 className="font-display font-semibold text-cc-ink">No communities yet</h3>
          <p className="text-sm text-cc-ink-soft mb-3">
            Create a group chat and bring your tribe together.
          </p>
          {onCreate && (
            <Button className="chitchat-btn-primary h-10 px-4" onClick={onCreate}>
              <Plus className="h-4 w-4 mr-1" /> Create community
            </Button>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {groups.map((g) => (
            <li key={g.id}>
              <button
                onClick={() => onOpen(g.id)}
                className="chitchat-surface w-full p-3 flex items-center gap-3 hover:bg-cc-surface-2 transition text-left"
              >
                <GradientAvatar id={g.id} name={g.name || "Group"} src={g.avatar_url} size={48} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-cc-ink truncate">
                    {g.name || "Untitled group"}
                  </div>
                  <div className="text-xs text-cc-ink-soft truncate">
                    {g.description || "Tap to open conversation"}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
